import React from "react";

// Inline SVG rather than a charting library: this is the only chart in the
// admin, and adding a dependency (plus its bundle weight) to draw ~26 points
// would not be a fair trade. Nothing here needs axes, tooltips or interaction.

const WIDTH = 90;
const HEIGHT = 24;
const PADDING = 2;

/**
 * Map values to SVG points.
 * `invert` is for position, where a LOWER number is better and must therefore
 * be drawn HIGHER on the chart. Getting this backwards would render every
 * ranking improvement as a decline, so it is explicit rather than implied.
 */
function toPoints(values, invert) {
  const usable = values.filter((v) => typeof v === "number" && Number.isFinite(v));
  if (usable.length < 2) return "";

  const min = Math.min(...usable);
  const max = Math.max(...usable);
  const span = max - min || 1;
  const step = (WIDTH - PADDING * 2) / (values.length - 1);

  return values
    .map((value, i) => {
      if (typeof value !== "number" || !Number.isFinite(value)) return null;
      const ratio = (value - min) / span;
      // Higher ratio should sit higher on screen, so y is flipped; `invert`
      // flips it back for position.
      const normalized = invert ? ratio : 1 - ratio;
      const x = PADDING + i * step;
      const y = PADDING + normalized * (HEIGHT - PADDING * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .filter(Boolean)
    .join(" ");
}

/**
 * @param {Array}   data    - series values (numbers, or null where unmeasured)
 * @param {boolean} invert  - true for position (lower is better)
 * @param {string}  color   - stroke colour
 */
const TrendSparkline = ({ data, invert = false, color = "#059669" }) => {
  const points = toPoints(data || [], invert);

  // One point is not a line. Saying so beats drawing a flat stroke that
  // implies "no change" when the truth is "not enough data yet".
  if (!points) {
    return (
      <span className="text-[10px] text-gray-400 italic">not enough data</span>
    );
  }

  return (
    <svg width={WIDTH} height={HEIGHT} className="overflow-visible" aria-hidden="true">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export default TrendSparkline;
